import { Navigate, Outlet } from 'react-router-dom';
import { BarChart3, FilePlus2, Home, Ticket } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { AppShell, type SidebarNavItem } from '@/components/AppShell';

/**
 * PortalLayout — sidebar shell for every /portal/* route.
 *
 * Role gate: if the authenticated user is not a CLIENT, redirect to /admin.
 * This keeps portal routes exclusive to clients; SUPPORT/ADMIN staff always
 * work through the admin shell.
 *
 * companyId guard: a valid CLIENT should always have a companyId (enforced
 * at the API layer), but we show a friendly message as defense-in-depth
 * for the rare edge case of a client whose company assignment is missing.
 *
 * Wrapped in <RequireAuth> at the router level so an active API-backed
 * session is required before this component renders.
 */

const NAV_ITEMS: readonly SidebarNavItem[] = [
  { key: 'home', label: 'Home', to: '/portal', icon: Home, end: true },
  { key: 'tickets', label: 'My Tickets', to: '/portal/tickets', icon: Ticket },
  { key: 'reports', label: 'Reports', to: '/portal/reports', icon: BarChart3 },
  { key: 'new-ticket', label: 'New Ticket', to: '/portal/tickets/new', icon: FilePlus2 },
];

export default function PortalLayout() {
  const { user, role, companyId } = useAuth();

  // Role gate — non-CLIENT users belong in the admin shell
  if (user && role !== null && role !== 'CLIENT') {
    return <Navigate to="/admin" replace />;
  }

  // companyId guard — defensive; should never happen for a valid CLIENT
  if (user && role === 'CLIENT' && !companyId) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center max-w-sm">
          <h2 className="text-lg font-semibold mb-2">No company assigned</h2>
          <p className="text-muted-foreground text-sm">
            No company assigned — please contact your admin to be linked to your company account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <AppShell title="Ventin Support" items={NAV_ITEMS} profileTo="/portal/profile">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Outlet />
      </div>
    </AppShell>
  );
}
